/**
 * Employees API hooks — TanStack Query layer over /api/employees.
 *
 * Server scoping rules (enforced in the handler):
 *   Admin    → all employees
 *   Manager  → direct reports + self
 *   Employee → self only
 *   Writes   → Admin only
 *   Directory → Active employees (limited fields) for all authenticated users
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api.js';
import type { Employee, EmployeeDirectoryEntry, Paginated } from '@shared/types';
import {
  employeeCreateInput,
  employeeUpdateInput,
} from '@shared/schemas/index.js';
import { z } from 'zod';
import type { TerminationInput } from '@shared/schemas/termination.js';
import type { Document } from '@shared/types';

export function useTerminateEmployee() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TerminationInput }) =>
      api.post<{ employee: Employee; document: Document }>(`/api/employees/${id}/terminate`, body),
    onSuccess: () => {
      for (const key of ['employees', 'documents', 'users', 'audit', 'dashboard']) {
        void client.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}

/** Payload types derived from the shared zod input schemas. */
export type EmployeeCreateBody = z.input<typeof employeeCreateInput>;
export type EmployeeUpdateBody = z.input<typeof employeeUpdateInput>;

export type EmployeeDirectoryItem = EmployeeDirectoryEntry;

export const employeeKeys = {
  all: ['employees'] as const,
  list: (params?: { search?: string; cursor?: string; limit?: number }) =>
    [...employeeKeys.all, 'list', params ?? {}] as const,
  directory: () => [...employeeKeys.all, 'directory'] as const,
  detail: (id: string) => [...employeeKeys.all, 'detail', id] as const,
};

/** List employees (optionally filtered by `search`). The server applies role-based scoping. */
export function useEmployees(
  params?: { search?: string; limit?: number },
  options?: { enabled?: boolean },
) {
  const query = { limit: 1000, ...params };
  return useQuery({
    queryKey: employeeKeys.list(query),
    queryFn: () =>
      api.get<Paginated<Employee>>(`/api/employees${qs(query)}`),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Lightweight Active-employee directory for pickers.
 * Available to every authenticated role (recognition, etc.).
 */
export function useEmployeeDirectory(
  options?: { enabled?: boolean; search?: string },
) {
  return useQuery({
    queryKey: [...employeeKeys.directory(), options?.search ?? ''],
    queryFn: () =>
      api.get<Paginated<EmployeeDirectoryItem>>(
        `/api/employees/directory${qs({ search: options?.search, limit: 1000 })}`,
      ),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });
}

/** Fetch a single employee by id. Disabled until `id` is provided. */
export function useEmployee(id: string | undefined) {
  return useQuery({
    queryKey: employeeKeys.detail(id ?? ''),
    queryFn: () => api.get<Employee>(`/api/employees/${id}`),
    enabled: Boolean(id),
  });
}

/** Create a new employee (Admin only). Invalidates the employees list on success. */
export function useCreateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: EmployeeCreateBody) =>
      api.post<Employee>('/api/employees', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/** Update an existing employee (Admin only). Patches the detail cache, then invalidates. */
export function useUpdateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: EmployeeUpdateBody }) =>
      api.put<Employee>(`/api/employees/${id}`, body),
    onSuccess: (updated) => {
      queryClient.setQueryData(employeeKeys.detail(updated.id), updated);
      void queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/** Delete an employee (Admin only). */
export function useDeleteEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean }>(`/api/employees/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
